import { ref, readonly, onMounted, onUnmounted } from 'vue'

type Breakpoint = 'mobile' | 'tablet' | 'desktop'

const BREAKPOINTS = {
  md: 768,
  lg: 1024,
} as const

/**
 * useBreakpoint - 반응형 브레이크포인트 감지 composable
 *
 * @returns isMobile (<768px), isTablet (768~1023px), isDesktop (>=1024px), breakpoint
 *
 * @example
 * ```vue
 * const { isMobile, isDesktop } = useBreakpoint()
 * // template: v-if="isMobile"
 * ```
 */
export function useBreakpoint() {
  const breakpoint = ref<Breakpoint>('desktop')
  const isMobile = ref(false)
  const isTablet = ref(false)
  const isDesktop = ref(true)

  let mdQuery: MediaQueryList | null = null
  let lgQuery: MediaQueryList | null = null

  function update() {
    const mdMatch = mdQuery?.matches ?? true
    const lgMatch = lgQuery?.matches ?? true

    if (!mdMatch) {
      breakpoint.value = 'mobile'
      isMobile.value = true
      isTablet.value = false
      isDesktop.value = false
    } else if (!lgMatch) {
      breakpoint.value = 'tablet'
      isMobile.value = false
      isTablet.value = true
      isDesktop.value = false
    } else {
      breakpoint.value = 'desktop'
      isMobile.value = false
      isTablet.value = false
      isDesktop.value = true
    }
  }

  onMounted(() => {
    if (typeof window === 'undefined') return

    mdQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.md}px)`)
    lgQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`)

    mdQuery.addEventListener('change', update)
    lgQuery.addEventListener('change', update)

    update()
  })

  onUnmounted(() => {
    mdQuery?.removeEventListener('change', update)
    lgQuery?.removeEventListener('change', update)
  })

  return {
    breakpoint: readonly(breakpoint),
    isMobile: readonly(isMobile),
    isTablet: readonly(isTablet),
    isDesktop: readonly(isDesktop),
  }
}
